import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { RootState } from '../reducers';
import { addTagToUserThunk } from '../features/settings/settingsSlice';

// Define the feature flag schema
export const featureFlagSchema = z.object({
  feature_enable_onboarding: z.enum(['true', 'false']),
  feature_unguard_upload: z.enum(['true', 'false']),
  feature_unguard_import_url: z.enum(['true', 'false']),
  feature_upgrade_variant_v2: z.enum(['true', 'false']),
  feature_support_variant_ai: z.enum(['true', 'false']), // Currently disabled - using team tags instead
  feature_hide_unlimited: z.enum(['true', 'false']),
  feature_chevron_below_price: z.enum(['true', 'false']),
  feature_hide_upgrade_left_column: z.enum(['true', 'false']),
  feature_higher_free_limits: z.enum(['true', 'false']),
});

// Derive TypeScript types from Zod schema
export type FeatureFlagKey = keyof z.infer<typeof featureFlagSchema>;
export type FeatureFlagValue = z.infer<
  typeof featureFlagSchema
>[FeatureFlagKey];

// Helper function to generate random boolean
const getRandomBoolean = () => Math.random() >= 0.5;

// Helper function to check if a key is a feature flag
const isFeatureFlag = (key: string): key is FeatureFlagKey => {
  return key.startsWith('feature_');
};

export const useBooleanFeature = () => {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.settings.user.tags);

  const getFeature = useCallback(
    (key: string): boolean => {
      // Winner: feature_enable_onboarding = false (disable onboarding for everyone)
      if (key === 'feature_enable_onboarding') {
        return false;
      }

      // Winner: feature_upgrade_variant_v2 = true (V2 upgrade modal for everyone)
      if (key === 'feature_upgrade_variant_v2') {
        return true;
      }

      // Winner: feature_hide_unlimited = true (hide unlimited option for everyone)
      if (key === 'feature_hide_unlimited') {
        return false;
      }

      // Winner: feature_unguard_upload = true (unguard upload for everyone)
      if (key === 'feature_unguard_upload') {
        return false;
      }

      // Winner: feature_unguard_import_url = true (unguard import URL for everyone)
      if (key === 'feature_unguard_import_url') {
        return false;
      }

      // Winner: feature_higher_free_limits = false (disable higher free limits for everyone)
      if (key === 'feature_higher_free_limits') {
        return false;
      }

      // Winner: feature_chevron_below_price = false (disable chevron below price for everyone)
      if (key === 'feature_chevron_below_price') {
        return false;
      }

      // Winner: feature_hide_upgrade_left_column = false (show upgrade left column for everyone)
      if (key === 'feature_hide_upgrade_left_column') {
        return false;
      }

      // If it's a defined feature flag
      if (isFeatureFlag(key)) {
        // If the feature flag exists in tags, return its value
        if (key in tags) {
          return tags[key] === 'true';
        }

        // If the feature flag doesn't exist, calculate a random value and set it
        const randomValue = getRandomBoolean();
        const stringValue = randomValue.toString() as FeatureFlagValue;
        dispatch(addTagToUserThunk({ key, value: stringValue }));
        return randomValue;
      }

      // For undefined feature flags, return random boolean without storing
      return getRandomBoolean();
    },
    [dispatch, tags]
  );

  return { getFeature };
};

export default useBooleanFeature;
